import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Bot, Play, TrendingUp, TrendingDown, DollarSign,
  BarChart3, RefreshCw, Zap, Target, FileText, Download, 
  Calendar, Clock, ArrowUpRight, ArrowDownRight, CheckCircle, XCircle
} from "lucide-react";

export default function AVBot() {
  const [status, setStatus] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [dailyReport, setDailyReport] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [allTimeReport, setAllTimeReport] = useState(null);
  const [activeReportTab, setActiveReportTab] = useState("all");

  const fetchAll = useCallback(async () => {
    try {
      const [statusRes, tradesRes, dailyRes, weeklyRes, monthlyRes, allTimeRes] = await Promise.all([
        api.get("/av-bot/status"),
        api.get("/av-bot/trades?limit=50"),
        api.get("/av-bot/daily-report?days=1"),
        api.get("/av-bot/daily-report?days=7"),
        api.get("/av-bot/daily-report?days=30"),
        api.get("/av-bot/daily-report?days=365"), // All time (1 year)
      ]);
      
      setStatus(statusRes.data);
      setTrades(tradesRes.data.trades || []);
      setDailyReport(dailyRes.data);
      setWeeklyReport(weeklyRes.data);
      setMonthlyReport(monthlyRes.data);
      setAllTimeReport(allTimeRes.data);
    } catch (err) {
      console.error("Error fetching AV Bot data:", err);
      toast.error("Failed to load AV Bot data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchAll]);

  const handleRun = async () => {
    setRunning(true);
    try {
      const res = await api.post("/av-bot/run");
      toast.success(res.data.message || "Trading cycle complete!");
      setTimeout(fetchAll, 5000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  const getReport = () => {
    if (activeReportTab === "daily") return dailyReport;
    if (activeReportTab === "weekly") return weeklyReport;
    if (activeReportTab === "monthly") return monthlyReport;
    return allTimeReport; // "all" - default
  };

  // Generate Professional PDF
  const handleExportPDF = () => {
    const report = getReport();
    if (!report) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;
    
    const periodLabel = activeReportTab === "daily" ? "Daily" : activeReportTab === "weekly" ? "Weekly" : activeReportTab === "monthly" ? "Monthly" : "All Time";

    // Header
    doc.setFillColor(16, 185, 129);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.text("AV AI BOT", pageWidth / 2, 18, { align: "center" });
    doc.setFontSize(14);
    doc.text(`${periodLabel} P&L Report`, pageWidth / 2, 28, { align: "center" });
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 36, { align: "center" });

    yPos = 50;

    // P&L Summary Box
    doc.setFillColor(240, 240, 240);
    doc.rect(14, yPos, pageWidth - 28, 35, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    
    const summary = report.summary || {};
    const totalPnl = summary.total_pnl || 0;
    const realizedPnl = summary.realized_pnl || 0;
    const unrealizedPnl = summary.unrealized_pnl || 0;
    
    doc.text("PROFIT & LOSS SUMMARY", 20, yPos + 10);
    doc.setFontSize(24);
    doc.setTextColor(totalPnl >= 0 ? 16 : 239, totalPnl >= 0 ? 185 : 68, totalPnl >= 0 ? 129 : 68);
    doc.text(`${totalPnl >= 0 ? '+' : ''}$${totalPnl.toLocaleString()}`, 20, yPos + 25);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Realized: $${realizedPnl.toLocaleString()} | Unrealized: $${unrealizedPnl.toLocaleString()}`, 20, yPos + 32);
    
    // Stats on right side
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`Win Rate: ${summary.win_rate || 0}%`, pageWidth - 60, yPos + 12);
    doc.text(`Wins: ${summary.wins || 0} | Losses: ${summary.losses || 0}`, pageWidth - 60, yPos + 22);
    doc.text(`Total Trades: ${summary.total_trades || 0}`, pageWidth - 60, yPos + 32);

    yPos += 45;

    // Open Positions
    if (report.open_positions?.length > 0) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text(`OPEN POSITIONS (${report.open_positions.length})`, 14, yPos);
      yPos += 5;

      const openData = report.open_positions.map(pos => {
        const currentPnl = pos.pnl || 0;
        const pnlPct = pos.pnl_pct || 0;
        return [
          pos.symbol,
          pos.direction,
          `$${pos.entry_price}`,
          pos.quantity,
          `$${pos.stop_loss}`,
          `$${pos.take_profit}`,
          `${currentPnl >= 0 ? '+' : ''}$${currentPnl.toFixed(2)}`,
          `${pnlPct >= 0 ? '+' : ''}${pnlPct.toFixed(1)}%`
        ];
      });

      autoTable(doc, {
        startY: yPos,
        head: [["Symbol", "Dir", "Entry", "Qty", "Stop Loss", "Target", "P&L $", "P&L %"]],
        body: openData,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { fontStyle: 'bold' },
          6: { fontStyle: 'bold' },
          7: { fontStyle: 'bold' }
        },
        didParseCell: function(data) {
          if ((data.column.index === 6 || data.column.index === 7) && data.section === 'body') {
            const val = parseFloat(data.cell.raw.replace(/[^-\d.]/g, ''));
            data.cell.styles.textColor = val >= 0 ? [16, 185, 129] : [239, 68, 68];
          }
        }
      });
      yPos = doc.lastAutoTable.finalY + 15;
    }

    // Closed Trades with Clear P&L
    if (report.closed_positions?.length > 0) {
      if (yPos > 220) { doc.addPage(); yPos = 20; }
      
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text(`CLOSED TRADES (${report.closed_positions.length})`, 14, yPos);
      yPos += 5;

      const closedData = report.closed_positions.map(pos => [
        new Date(pos.executed_at || pos.closed_at).toLocaleDateString(),
        pos.symbol,
        pos.direction,
        `$${pos.entry_price}`,
        `$${pos.exit_price || '-'}`,
        pos.quantity,
        `${(pos.pnl || 0) >= 0 ? '+' : ''}$${(pos.pnl || 0).toFixed(2)}`,
        `${(pos.pnl_pct || 0) >= 0 ? '+' : ''}${(pos.pnl_pct || 0).toFixed(1)}%`,
        pos.close_reason || '-'
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [["Date", "Symbol", "Dir", "Entry", "Exit", "Qty", "Profit/Loss", "Return", "Reason"]],
        body: closedData,
        theme: 'grid',
        headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: { 
          0: { cellWidth: 22 },
          6: { fontStyle: 'bold' },
          7: { fontStyle: 'bold' }
        },
        didParseCell: function(data) {
          if ((data.column.index === 6 || data.column.index === 7) && data.section === 'body') {
            const val = parseFloat(data.cell.raw.replace(/[^-\d.]/g, ''));
            data.cell.styles.textColor = val >= 0 ? [16, 185, 129] : [239, 68, 68];
          }
        }
      });
      yPos = doc.lastAutoTable.finalY + 15;
    }

    // All Trades Log
    if (report.all_trades?.length > 0) {
      if (yPos > 180) { doc.addPage(); yPos = 20; }
      
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("TRADE LOG", 14, yPos);
      yPos += 5;

      const tradeLogData = report.all_trades.slice(0, 30).map(t => [
        new Date(t.executed_at).toLocaleDateString(),
        t.symbol,
        t.direction,
        `$${t.entry_price}`,
        `$${(t.trade_value || 0).toLocaleString()}`,
        `${t.conviction}%`,
        t.signal_source || '-',
        (t.reasoning || '').substring(0, 30) + '...'
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [["Date", "Symbol", "Dir", "Price", "Value", "Conf", "Source", "Signal"]],
        body: tradeLogData,
        theme: 'striped',
        headStyles: { fillColor: [75, 85, 99], textColor: 255 },
        styles: { fontSize: 7 },
      });
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text(
        `Page ${i}/${pageCount} | AV AI Bot ${periodLabel} Report | SP Wealth Tracker`,
        pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" }
      );
    }

    doc.save(`AV_Bot_${periodLabel}_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success(`${periodLabel} report downloaded!`);
  };

  const config = status?.config || {};
  const portfolio = status?.portfolio || {};
  const isEnabled = config.enabled;
  const report = getReport();
  const summary = report?.summary || {};
  
  // Use all-time report for main stats if available
  const allTimeSummary = allTimeReport?.summary || {};

  if (loading) {
    return (
      <div className="p-4 space-y-4" data-testid="av-bot-loading">
        <div className="h-32 skeleton rounded-lg" />
        <div className="h-64 skeleton rounded-lg" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6" data-testid="av-bot-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2">
            <Bot className="w-7 h-7 text-emerald-400" />
            AV AI BOT
          </h1>
          <p className="text-sm text-muted-foreground">Autonomous Trading • Clear P&L Tracking</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={isEnabled ? "bg-emerald-500" : "bg-gray-500"}>
            {isEnabled ? "ACTIVE" : "PAUSED"}
          </Badge>
          <Button onClick={handleRun} disabled={running} size="sm" className="bg-emerald-500 hover:bg-emerald-600">
            {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* MAIN P&L DASHBOARD - Super Clear */}
      <Card className="card-dark border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-transparent">
        <CardContent className="p-6">
          <div className="text-center mb-6">
            <p className="text-sm text-muted-foreground mb-1">TOTAL PORTFOLIO VALUE</p>
            <p className="text-4xl font-bold font-data text-white">
              ${(portfolio.total_value || config.capital || 100000).toLocaleString()}
            </p>
            <p className={`text-xl font-bold mt-1 ${(portfolio.total_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {(portfolio.total_pnl || 0) >= 0 ? '+' : ''}${(portfolio.total_pnl || 0).toLocaleString()}
              <span className="text-sm ml-2">({(portfolio.total_pnl_pct || 0).toFixed(2)}%)</span>
            </p>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-4 gap-3 text-center">
            <div className="bg-black/30 rounded-lg p-3">
              <p className="text-2xl font-bold text-emerald-400">${(portfolio.cash || 0).toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Cash Available</p>
            </div>
            <div className="bg-black/30 rounded-lg p-3">
              <p className="text-2xl font-bold text-blue-400">${(portfolio.invested || 0).toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Invested</p>
            </div>
            <div className="bg-black/30 rounded-lg p-3">
              <p className="text-2xl font-bold">{portfolio.holdings?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Open Positions</p>
            </div>
            <div className="bg-black/30 rounded-lg p-3">
              <p className="text-2xl font-bold text-yellow-400">{allTimeSummary.win_rate || summary.win_rate || 0}%</p>
              <p className="text-xs text-muted-foreground">Win Rate</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* P&L BREAKDOWN TABS - Daily/Weekly/Monthly */}
      <Card className="card-dark">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-400" />
              P&L Breakdown
            </CardTitle>
            <Button onClick={handleExportPDF} size="sm" className="gap-1 bg-blue-500 hover:bg-blue-600">
              <Download className="w-4 h-4" /> Download PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeReportTab} onValueChange={setActiveReportTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-4">
              <TabsTrigger value="all" className="gap-1">
                <BarChart3 className="w-3 h-3" /> All Time
              </TabsTrigger>
              <TabsTrigger value="monthly" className="gap-1">
                <Calendar className="w-3 h-3" /> Month
              </TabsTrigger>
              <TabsTrigger value="weekly" className="gap-1">
                <Calendar className="w-3 h-3" /> Week
              </TabsTrigger>
              <TabsTrigger value="daily" className="gap-1">
                <Calendar className="w-3 h-3" /> Today
              </TabsTrigger>
            </TabsList>

            {/* P&L Summary for Selected Period */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <div className="bg-black/20 rounded-lg p-3 text-center border-l-4 border-l-emerald-500">
                <p className={`text-xl font-bold ${(summary.total_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {(summary.total_pnl || 0) >= 0 ? '+' : ''}${(summary.total_pnl || 0).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">Total P&L</p>
              </div>
              <div className="bg-black/20 rounded-lg p-3 text-center">
                <p className={`text-xl font-bold ${(summary.realized_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {(summary.realized_pnl || 0) >= 0 ? '+' : ''}${(summary.realized_pnl || 0).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">Realized</p>
              </div>
              <div className="bg-black/20 rounded-lg p-3 text-center">
                <p className="text-xl font-bold">
                  <span className="text-emerald-400">{summary.wins || 0}</span>
                  <span className="text-muted-foreground mx-1">/</span>
                  <span className="text-red-400">{summary.losses || 0}</span>
                </p>
                <p className="text-xs text-muted-foreground">Wins / Losses</p>
              </div>
              <div className="bg-black/20 rounded-lg p-3 text-center">
                <p className="text-xl font-bold">{summary.total_trades || 0}</p>
                <p className="text-xs text-muted-foreground">Trades</p>
              </div>
            </div>
          </Tabs>
        </CardContent>
      </Card>

      {/* OPEN POSITIONS - Clear Entry/Target/Stop */}
      {portfolio.holdings?.length > 0 && (
        <Card className="card-dark">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-400" />
              Open Positions ({portfolio.holdings.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {portfolio.holdings.map((pos, idx) => {
              const isLong = pos.direction === "BUY";
              const pnl = pos.pnl || 0;
              const pnlPct = pos.pnl_pct || 0;
              
              return (
                <div
                  key={pos.trade_id || idx}
                  className={`p-4 rounded-lg border-l-4 ${isLong ? 'border-l-emerald-500 bg-emerald-500/5' : 'border-l-red-500 bg-red-500/5'}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-lg">{pos.symbol}</span>
                      <Badge className={isLong ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                        {isLong ? <ArrowUpRight className="w-3 h-3 mr-1" /> : <ArrowDownRight className="w-3 h-3 mr-1" />}
                        {pos.direction}
                      </Badge>
                      <Badge variant="outline" className="text-xs">{pos.asset_class}</Badge>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
                      </p>
                      <p className={`text-sm ${pnlPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%
                      </p>
                    </div>
                  </div>
                  
                  {/* Entry/Stop/Target - Super Clear */}
                  <div className="grid grid-cols-4 gap-2 text-center bg-black/20 rounded-lg p-2">
                    <div>
                      <p className="text-xs text-muted-foreground">ENTRY</p>
                      <p className="font-bold font-data">${pos.entry_price}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">QTY</p>
                      <p className="font-bold font-data">{pos.quantity}</p>
                    </div>
                    <div>
                      <p className="text-xs text-red-400">STOP LOSS</p>
                      <p className="font-bold font-data text-red-400">${pos.stop_loss}</p>
                    </div>
                    <div>
                      <p className="text-xs text-emerald-400">TARGET</p>
                      <p className="font-bold font-data text-emerald-400">${pos.take_profit}</p>
                    </div>
                  </div>
                  
                  {/* Signal Info */}
                  <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <Zap className="w-3 h-3" />
                    <span className="text-blue-400">{pos.signal_source}</span>
                    <span>•</span>
                    <span>{pos.reasoning?.substring(0, 60)}...</span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Opened: {new Date(pos.executed_at).toLocaleString()}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* CLOSED TRADES - Clear Profit/Loss per Trade */}
      {report?.closed_positions?.length > 0 && (
        <Card className="card-dark">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-purple-400" />
              Closed Trades ({report.closed_positions.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {report.closed_positions.slice(0, 10).map((pos, idx) => {
              const pnl = pos.pnl || 0;
              const pnlPct = pos.pnl_pct || 0;
              const isWin = pnl >= 0;
              
              return (
                <div
                  key={pos.trade_id || idx}
                  className={`p-3 rounded-lg flex items-center justify-between ${isWin ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}
                >
                  <div className="flex items-center gap-3">
                    {isWin ? (
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                    <div>
                      <p className="font-bold">{pos.symbol}</p>
                      <p className="text-xs text-muted-foreground">
                        {pos.direction} • Entry: ${pos.entry_price} → Exit: ${pos.exit_price || '-'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${isWin ? 'text-emerald-400' : 'text-red-400'}`}>
                      {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
                    </p>
                    <p className={`text-xs ${isWin ? 'text-emerald-400' : 'text-red-400'}`}>
                      {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}%
                    </p>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* RECENT TRADE LOG */}
      <Card className="card-dark">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-400" />
            Trade Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {trades.slice(0, 20).map((t, idx) => (
              <div key={t.trade_id || idx} className="p-2 bg-black/20 rounded flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">
                    {new Date(t.executed_at).toLocaleDateString()}
                  </span>
                  <Badge className={t.direction === 'BUY' ? 'bg-emerald-500/20 text-emerald-400 text-xs' : 'bg-red-500/20 text-red-400 text-xs'}>
                    {t.direction}
                  </Badge>
                  <span className="font-bold">{t.symbol}</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span>${t.entry_price}</span>
                  <span className="text-muted-foreground">×{t.quantity}</span>
                  <span className="text-blue-400">{t.conviction}%</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
